import { useQueryClient } from "@tanstack/react-query";

import { subscriptionQueryOptions } from "@/domains/subscriptions/factories/subscription-query-options";
import { useSubscriptionLink } from "@/domains/subscriptions/queries/use-subscription-link";
import { Button, icons, Modal } from "@/ui";

type ManageSubscriptionModalProps = {
  isOpen: boolean;
  closeModal: () => void;
};

export const ManageSubscriptionModal = ({ closeModal, isOpen }: ManageSubscriptionModalProps) => {
  const queryClient = useQueryClient();
  const { data, isError, isFetching, refetch } = useSubscriptionLink(isOpen);

  const handleClose = () => {
    queryClient.removeQueries({ queryKey: subscriptionQueryOptions.link.queryKey });
    closeModal();
  };

  const goLabel = (
    <span className="flex items-center gap-1">
      Go
      <icons.ArrowRight className="size-5" />
    </span>
  );

  const renderAction = () => {
    /*
      A plain link, with the URL already fetched when the modal opened: Safari
      blocks a window.open that waits on a request after the click.
    */
    if (data?.link) {
      return (
        <Button className="w-full justify-center" asChild>
          <a href={data.link} onClick={handleClose} rel="noopener noreferrer" target="_blank">
            {goLabel}
          </a>
        </Button>
      );
    }

    if (isError) {
      return (
        <Button
          className="w-full justify-center"
          isLoading={isFetching}
          onClick={() => {
            void refetch();
          }}
        >
          Try Again
        </Button>
      );
    }

    return (
      <Button className="w-full justify-center" isLoading>
        {goLabel}
      </Button>
    );
  };

  return (
    <Modal
      className="text-center"
      closeModal={handleClose}
      isOpen={isOpen}
      title={isError ? "Unable to Load Portal" : "You're Being Routed to Your Subscription Manager"}
    >
      <div className="flex flex-col items-center gap-6 px-6 pb-10">{renderAction()}</div>
    </Modal>
  );
};
